import * as React from 'react';

import { TextField } from '@rematter/ui-web';
import { GitBranch } from 'lucide-react';

import { ExpressionInput } from '../../../shared/ExpressionInput';
import { NodeConfigProps } from '../../types';

import { ConditionConfigType } from './schema';

/**
 * Configuration panel for Condition nodes.
 * Allows configuring conditional expressions and branch labels.
 */
export const ConditionConfig: React.FC<
  NodeConfigProps<ConditionConfigType>
> = ({ config, onUpdate }) => {
  const [expression, setExpression] = React.useState<string>(
    config.expression || '',
  );
  const [trueBranchLabel, setTrueBranchLabel] = React.useState<string>(
    config.trueBranchLabel || 'Yes',
  );
  const [falseBranchLabel, setFalseBranchLabel] = React.useState<string>(
    config.falseBranchLabel || 'No',
  );

  const handleUpdate = React.useCallback(
    (updates: Partial<ConditionConfigType>) => {
      // Include the condition expression at root level for backend compatibility
      const newConfig = { ...config, ...updates };
      onUpdate({
        ...newConfig,
        // Store expression also at config level for display
        ...(updates.expression !== undefined && {
          _rootExpression: updates.expression,
        }),
      } as ConditionConfigType);
    },
    [config, onUpdate],
  );

  const handleExpressionChange = React.useCallback(
    (value: string) => {
      setExpression(value);
      handleUpdate({ expression: value });
    },
    [handleUpdate],
  );

  const handleTrueBranchChange = React.useCallback(
    (value: string) => {
      setTrueBranchLabel(value);
      handleUpdate({ trueBranchLabel: value });
    },
    [handleUpdate],
  );

  const handleFalseBranchChange = React.useCallback(
    (value: string) => {
      setFalseBranchLabel(value);
      handleUpdate({ falseBranchLabel: value });
    },
    [handleUpdate],
  );

  return (
    <div className="@ConditionConfig space-y-4">
      <div>
        <ExpressionInput
          label="Condition Expression"
          value={expression}
          onChange={handleExpressionChange}
          placeholder="trigger.data.status == 'active'"
          description="Expression that evaluates to true or false"
        />
      </div>

      <div className="rounded-lg border bg-neutral/5 p-3">
        <p className="mb-2 text-xs font-medium">Expression Examples:</p>
        <div className="space-y-1 font-mono text-xs text-neutral/70">
          <div>
            <code>trigger.data.amount &gt; 1000</code>
          </div>
          <div>
            <code>trigger.data.status == "approved"</code>
          </div>
          <div>
            <code>len(trigger.data.items) &gt; 0</code>
          </div>
          <div>
            <code>trigger.data.priority == "high" && trigger.data.urgent</code>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="mb-3 flex items-center gap-2">
          <GitBranch className="size-4 text-neutral/60" />
          <p className="text-sm font-medium">Branch Labels</p>
        </div>

        <div className="space-y-3">
          <TextField
            label="True Branch Label"
            value={trueBranchLabel}
            onChange={handleTrueBranchChange}
            placeholder="Yes"
            description="Label for the path when condition is true"
          />

          <TextField
            label="False Branch Label"
            value={falseBranchLabel}
            onChange={handleFalseBranchChange}
            placeholder="No"
            description="Label for the path when condition is false"
          />
        </div>
      </div>

      <div className="rounded-lg border border-primary/20 bg-primary/10 p-3">
        <p className="text-xs text-primary">
          <strong>Tip:</strong> Access trigger data with{' '}
          <code className="rounded bg-primary/20 px-1">
            trigger.data.fieldName
          </code>{' '}
          and previous action outputs with{' '}
          <code className="rounded bg-primary/20 px-1">
            outputs.nodeId.fieldName
          </code>
          .
        </p>
      </div>

      <div className="rounded-lg border border-warning/20 bg-warning/10 p-3">
        <p className="text-xs text-warning">
          <strong>Operators:</strong> Use ==, !=, {'<'}, {'>'}, {'<='}, {'>='},
          &&, ||, ! for comparisons. Use len() for array/string length.
        </p>
      </div>
    </div>
  );
};
